import type { AppState } from '../../store';
import {
  DOC_BEAN_COLOR,
  DOC_BEAN_ORDER,
  DOC_ID_FIELD_BY_KEY,
  DOC_KINDS,
  docIconId,
  docPillLabel,
} from '../../lib/docs';
import { CUSTOM_DOC_ROWS_SHOWN } from '../../lib/layout/constants';
import { nodeHasActivePipelineStage, nodeMatchesSearch } from '../../lib/search';
import { presenceText } from '../../i18n/presence';
import type { DocId, DocKindKey, DocPerm, NodeId } from '../../lib/types';
import { OWED_CUSTOM_LABEL, OWED_PILL_LABEL, plannedMissing, plannedMissingCount } from '../../lib/health';
import type { PresenceUser } from '../presence/PresenceStack';
import { mapContextOf } from './mapContext';
import { posY } from './posY';

/**
 * Everything one map node renders, flattened to primitives.
 *
 * WHY FLAT, AND WHY THIS EXISTS AT ALL. `MapNode` subscribes with `useShallow`, so this
 * object IS the re-render decision: the node re-renders exactly when one of these values
 * differs from last time. That gives the property the plan asks to be proved — a label edit
 * re-renders one node, a pan re-renders none — and it gives it without depending on object
 * identity, which is the one signal this migration cannot use. The vanilla script mutates
 * `nodes[id]` IN PLACE, so `s.nodes[id]` is the same reference before and after a rename;
 * subscribing to it would show a stale label for ever.
 *
 * The compiler is the enforcement. `MapNode` receives this and nothing else, so it cannot
 * read a field that is not part of the re-render decision — which is the failure a
 * "signature string plus a getState() read" arrangement invites and never announces.
 *
 * TWO FIELDS ARE ENCODED RATHER THAN FLAT, and it is the honest trade. `metaColors` and
 * `beansJson` are lists, and a fresh array fails a shallow compare every time however
 * unchanged its contents are. They are joined / stringified here and parsed back in the
 * component: a value comparison in the only form a shallow compare understands. `U+0000` is
 * the separator because no title can contain it.
 */

export const META_SEP = '\u0000';

/** One documentation pill under a node, in the order they are shown. */
export interface BeanRow {
  /** The slot this pill hangs off, or null for a custom document. */
  key: DocKindKey | null;
  docId: DocId | null;
  /** Only set for the "+N more" stand-in, which has no page behind it. */
  more?: number;
  label: string;
  color: string;
  /** Symbol id for the type icon, or null where there is no page to have a type. */
  icon: string | null;
  /**
   * A PLANNED-but-missing pill (V0.42): the plan names this document and it is not attached.
   * Dashed, unfilled, in the kind's colour; clicking it CREATES the page (for whoever may).
   * `kindPerm` is the DocPerm to create — the four fixed kinds, or 'custom'.
   */
  planned?: boolean;
  kindPerm?: DocPerm;
}

export interface NodeView {
  present: boolean;
  root: boolean;
  label: string;
  /** `n.color`, exactly as stored — the toggle only tints itself when there IS one. */
  rawColor: string | null;
  /** `n.color || '#5b8cff'`, which is what the box is painted from. */
  color: string;
  x: number;
  y: number;
  side: number;
  collapsed: boolean;
  hasChildren: boolean;
  isLight: boolean;

  hasDesc: boolean;
  /** Colours of the meta dots after the description dot, `U+0000`-joined. */
  metaColors: string;

  tagHit: boolean;
  tagDim: boolean;
  hlColor: string;
  searchHit: boolean;
  searchDim: boolean;
  statusHit: boolean;
  statusDim: boolean;
  dsColor: string;
  chatHit: boolean;
  chatDim: boolean;
  /** The eye assignment filter (V0.42), the fifth layer: this node carries one of my
   *  assignments (hit), or is dimmed because another does and this does not. */
  assignHit: boolean;
  assignDim: boolean;

  /**
   * The clearance heatmap: the node's bucket key, or `''` for "keep your own colour" — which
   * is every node while the mode is off, and the ROOT always (V0.42: with the mode on, every
   * other node takes a bucket — nothing keeps its own colour). A PRIMITIVE on purpose:
   * `styleFor` changes only its colour INPUT (`heat ? HEAT_HEX[heat] : color`), so the heat
   * composes with the four highlight layers above with no new class, and `useShallow` keeps
   * short-circuiting across publishes.
   */
  heat: string;
  /** The `<symbol>` id of the bucket's badge (V0.42), `''` for slate and no-heat. */
  heatIcon: string;
  /** The tooltip sentence for the bucket, `''` when there is no heat. Stable per data. */
  heatTitle: string;

  /** True when the zoomed-in expanded rows are showing instead of the small blobs. */
  expandedRows: boolean;
  /** `BeanRow[]`, stringified. See the note above. */
  beansJson: string;
  /** Custom documents beyond the pills, for the zoomed-out "+N" badge. */
  customCount: number;

  /** Presence badges: `userId|initials|colour|avatarV|editing|title`, one per socket, joined. */
  presenceJson: string;

  /** Interaction chrome, per node so a drag does not re-render its neighbours. */
  dragging: boolean;
  dropTarget: boolean;
  /** The node is in a popup's seat, a page's bubble, or in flight between them (V0.47). */
  away: boolean;
  linkSel: boolean;

  /**
   * May this user restructure the map (V0.37: `EDIT_MAP`). Global, but carried per node
   * because this object IS the render decision: it gates the delete cross, which must not be
   * drawn for a participant whose click on it the pointer handler would refuse anyway.
   */
  mapEditable: boolean;

  /**
   * A PROJECT node (V0.50.3): the flag mark drawn between the swatch and the label — inside the
   * box, because all four corners are taken (heat badge, presence, meta dots, doc pills). From
   * `MapNode.project`, which rides the map payload for every reader.
   */
  project: boolean;
  /**
   * The subtree's to-dos done / total, for the progress underline drawn in HEATMAP MODE only.
   * -1 when the bar is not drawn: mode off, not a project, or no figures for this reader (they
   * come from `GET /api/projects`, which needs VIEW_TODOS — the mark alone is for everyone).
   */
  projectDone: number;
  projectTotal: number;
}

/** "editing “Orders”" / "viewing a document" / "on the map" — `presenceActivity()`, verbatim. */
function presenceActivity(s: AppState, u: PresenceUser): string {
  const loc = u.loc ?? {};
  const t = (k: Parameters<typeof presenceText>[0]) => presenceText(k, s.currentLocale);
  const verb = loc.mode === 'edit' ? t('editing') : t('viewing');
  if (loc.docId) {
    const title = s.docTitles[loc.docId];
    return `${verb} ${title ? `“${title}”` : t('adoc')}`;
  }
  if (loc.nodeId) {
    const n = s.nodes[loc.nodeId];
    return `${verb} ${n ? `“${n.label}”` : t('anode')}`;
  }
  return t('onmap');
}

/** Placeholder for a node that has gone — React can render one more frame after a delete. */
const ABSENT: NodeView = {
  present: false,
  root: false,
  label: '',
  rawColor: null,
  color: '#5b8cff',
  x: 0,
  y: 0,
  side: 1,
  collapsed: false,
  hasChildren: false,
  isLight: false,
  hasDesc: false,
  metaColors: '',
  tagHit: false,
  tagDim: false,
  hlColor: '',
  searchHit: false,
  searchDim: false,
  statusHit: false,
  statusDim: false,
  dsColor: '',
  chatHit: false,
  chatDim: false,
  assignHit: false,
  assignDim: false,
  heat: '',
  heatIcon: '',
  heatTitle: '',
  expandedRows: false,
  beansJson: '[]',
  customCount: 0,
  presenceJson: '',
  dragging: false,
  dropTarget: false,
  away: false,
  linkSel: false,
  mapEditable: false,
  project: false,
  projectDone: -1,
  projectTotal: -1,
};

/**
 * The documentation pills a node shows, in `DOC_BEAN_ORDER` and gated per group.
 *
 * Exported because the row COUNT here and `docExtraHeight()`'s must agree exactly — the
 * layout reserves space for the rows this produces, and if the two disagree the expanded map
 * silently overlaps. Both read `CUSTOM_DOC_ROWS_SHOWN` for the "+N" collapse.
 */
export function beanRowsOf(s: AppState, id: NodeId, expanded: boolean): BeanRow[] {
  const n = s.nodes[id];
  if (!n) return [];
  const ctx = mapContextOf(s);
  const rows: BeanRow[] = [];

  for (const key of DOC_BEAN_ORDER) {
    const kind = DOC_KINDS.find((d) => d.key === key)!;
    const docId = n[DOC_ID_FIELD_BY_KEY[key]] as DocId | null | undefined;
    if (!ctx.canDocBtn(kind.perm) || !docId) continue;
    rows.push({
      key,
      docId,
      // Zoomed out the pill is a density indicator, not a label: colour alone does that job
      // and 10px has no room for text, so the label is only ever read when expanded.
      label: expanded
        ? docPillLabel(docId, kind.label, { docTitles: s.docTitles, docTypes: s.docTypes })
        : kind.label,
      color: DOC_BEAN_COLOR[key],
      icon: expanded ? docIconId(s.docTypes, docId) : null,
    });
  }

  // Planned pills and custom rows only make sense in the expanded view; collapsed, the node
  // shows its attached fixed slots as small blobs and nothing else.
  if (!expanded) return rows;

  if (ctx.canDocBtn('custom')) {
    const custom = n.customDocs ?? [];
    for (const docId of custom.slice(0, CUSTOM_DOC_ROWS_SHOWN)) {
      rows.push({
        key: null,
        docId,
        label: docPillLabel(docId, 'Untitled', { docTitles: s.docTitles, docTypes: s.docTypes }),
        color: 'var(--ink-faint)',
        icon: docIconId(s.docTypes, docId),
      });
    }
    if (custom.length > CUSTOM_DOC_ROWS_SHOWN) {
      rows.push({
        key: null,
        docId: null,
        more: custom.length - CUSTOM_DOC_ROWS_SHOWN,
        label: `+${custom.length - CUSTOM_DOC_ROWS_SHOWN} more`,
        color: 'var(--ink-faint)',
        icon: null,
      });
    }
  }

  // PLANNED-but-missing dotted pills (V0.42), appended after everything attached. SHOWN only
  // in edit or heatmap mode, and only to someone who could create them (`ctx.showPlanned`) —
  // a view-only reader's map is unchanged. Counted the same way by `expandedRows` and
  // `docExtraHeight` (both gate on the same predicate) — do not add a row here without the
  // count following.
  if (!ctx.showPlanned(id)) return rows;
  const pm = plannedMissing(n, ctx.canDocBtn);
  for (const perm of pm.kinds) {
    const kind = DOC_KINDS.find((d) => d.perm === perm)!;
    rows.push({
      key: kind.key,
      docId: null,
      planned: true,
      kindPerm: perm,
      label: OWED_PILL_LABEL(kind.label),
      color: DOC_BEAN_COLOR[kind.key],
      icon: null,
    });
  }
  for (let i = 0; i < pm.customMissing; i++) {
    rows.push({
      key: null,
      docId: null,
      planned: true,
      kindPerm: 'custom',
      label: OWED_PILL_LABEL(OWED_CUSTOM_LABEL),
      color: 'var(--ink-faint)',
      icon: null,
    });
  }
  return rows;
}

export function buildNodeView(s: AppState, id: NodeId): NodeView {
  const n = s.nodes[id];
  if (!n) return ABSENT;
  const ctx = mapContextOf(s);
  const root = !!n.root;

  // Meta dots: the description dot, then one per VISIBLE tag. An invisible tag stays hidden
  // from non-editors here too — an admin can tag a branch without it leaving a trace.
  const metaColors: string[] = [];
  for (const tagId of n.tags ?? []) {
    const t = s.tags[tagId];
    if (!t) continue;
    if (t.invisible && !ctx.showsInvisibleTags) continue;
    metaColors.push(t.color ?? '');
  }

  // The first of this node's tags the multi-tag filter reaches decides the highlight colour.
  const tagFilterOn = s.activeTags.size > 0;
  let tagHitColor = '';
  if (tagFilterOn) {
    for (const t of n.tags ?? []) {
      const c = ctx.wantedTags.get(t);
      if (c !== undefined) {
        tagHitColor = c;
        break;
      }
    }
  }
  const tagHit = tagHitColor !== '';
  const searchMatch = ctx.searchOn && nodeMatchesSearch(n, ctx.filter);
  const statusHit = !!s.activeDocStatus && nodeHasActivePipelineStage(n, ctx.filter);
  const chatOn = s.chatHighlight.size > 0;
  const chatHit = chatOn && s.chatHighlight.has(id);
  const assignOn = s.assignHighlight.size > 0;
  const assignHit = assignOn && s.assignHighlight.has(id);

  const custom = ctx.canDocBtn('custom') ? (n.customDocs ?? []) : [];
  const customCount = custom.length;
  const shownCustom =
    Math.min(customCount, CUSTOM_DOC_ROWS_SHOWN) + (customCount > CUSTOM_DOC_ROWS_SHOWN ? 1 : 0);
  const fixedCount = DOC_BEAN_ORDER.filter((key) => {
    const kind = DOC_KINDS.find((d) => d.key === key)!;
    return ctx.canDocBtn(kind.perm) && n[DOC_ID_FIELD_BY_KEY[key]];
  }).length;
  // Planned-but-missing pills add rows too (V0.42), but only where they are SHOWN (edit or
  // heatmap mode, for someone who could create them) — counted by the SAME predicate and helper
  // `docExtraHeight` uses. Whether rows show AT ALL is `docsExpanded` and nothing else
  // (V0.42.2): the zoom threshold and the corner toggle are folded together upstream, in
  // `evaluateDocsExpand()`, so this cannot disagree with the layout that made room for them.
  const plannedCount = ctx.showPlanned(id) ? plannedMissingCount(n, ctx.canDocBtn) : 0;
  const expandedRows = !root && s.docsExpanded && fixedCount + shownCustom + plannedCount >= 1;

  const badges = ctx.presence.get(id) ?? [];

  return {
    present: true,
    root,
    label: n.label,
    rawColor: n.color ?? null,
    color: n.color || '#5b8cff',
    x: s.positions[id]?.x ?? 0,
    y: posY(s, id),
    side: n.side,
    collapsed: !!n.collapsed,
    hasChildren: ctx.graph.hasChildren(id),
    isLight: s.isLight,

    hasDesc: !!(n.desc && n.desc.trim()),
    metaColors: metaColors.join(META_SEP),

    // The highlight layers, in the order the original applies them: each later one removes the
    // dim class the previous one may have added, so only the LAST active layer dims anything.
    // The eye assignment layer (V0.42) is inserted LAST, so every earlier dim also excludes
    // `assignHit` — an assigned node lights rather than dims under any other filter.
    tagHit,
    tagDim: tagFilterOn && !tagHit && !root && !(searchMatch || statusHit || chatHit || assignHit),
    hlColor: tagHitColor,
    searchHit: searchMatch,
    searchDim: ctx.searchOn && !searchMatch && !root && !(statusHit || chatHit || assignHit),
    statusHit,
    statusDim: !!s.activeDocStatus && !statusHit && !root && !(chatHit || assignHit),
    dsColor: statusHit ? s.activeDocStatus ?? '' : '',
    chatHit,
    chatDim: chatOn && !chatHit && !root && !assignHit,
    assignHit,
    assignDim: assignOn && !assignHit && !root,

    // The heat is read from the ONE index the context built for this publish — never computed
    // here, where it would run once per node per notification.
    heat: ctx.heat.byNode.get(id)?.heat ?? '',
    heatIcon: ctx.heat.byNode.get(id)?.icon ?? '',
    heatTitle: ctx.heat.byNode.get(id)?.title ?? '',

    expandedRows,
    beansJson: JSON.stringify(beanRowsOf(s, id, expandedRows)),
    customCount,

    // The tooltip is resolved HERE rather than in the component, because it needs the doc
    // titles, the node labels and the locale — three things the component would otherwise
    // have to subscribe to, per node, to render a string almost nobody hovers.
    presenceJson: badges.length
      ? JSON.stringify(
          badges.map((b) => ({
            userId: b.user.userId,
            initials: b.user.initials ?? '?',
            color: b.user.color ?? '',
            avatarV: b.user.avatarV ?? null,
            editing: b.editing,
            displayName: b.user.displayName ?? '',
            title: `${b.user.displayName || ''} · ${presenceActivity(s, b.user)}`,
          })),
        )
      : '',

    dragging: s.dragNodeId === id,
    dropTarget: s.dropTargetId === id,
    away: s.perch !== null && s.perch.id === id,
    linkSel: s.linkFromId === id,
    mapEditable: ctx.canEditMap,

    // The project mark rides the payload; the figures behind the heatmap bar are this reader's
    // own `GET /api/projects` rollup and exist only for a VIEW_TODOS holder with the mode on.
    project: !root && !!n.project,
    projectDone: !root && n.project && s.heatmapOn && s.projectProgress[id] ? s.projectProgress[id].done : -1,
    projectTotal: !root && n.project && s.heatmapOn && s.projectProgress[id] ? s.projectProgress[id].total : -1,
  };
}
